// As abelhas que voam no site (site/home/home.js), atrás do cartão da entrada da versão web (AC-257):
// mesmo desenho e mesmo voo, 15% maiores, deixando o rastro de ✓ que some. Sobre o amarelo mel, o
// corpo ganha um contorno escuro para não sumir no fundo. Com "reduzir movimento", ficam paradas.
import { useEffect, useRef } from 'react';

const SIZE = 0.75 * 1.15; // o tamanho do site, 15% maior (pedido do usuário em 04/11/2026)

interface Bee {
  x: number;
  y: number;
  a: number;
  v: number;
  turn: number;
  seed: number;
  drop: number;
}

interface Check {
  x: number;
  y: number;
  t: number;
}

export function BeeField() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    let ctx: CanvasRenderingContext2D | null = null;
    try {
      ctx = canvas?.getContext('2d') ?? null;
    } catch {
      ctx = null;
    }
    if (!canvas || !ctx) return;
    const g = ctx;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    let W = 0;
    let H = 0;
    let bees: Bee[] = [];
    let checks: Check[] = [];
    let last = performance.now();
    let raf = 0;

    const makeBee = (): Bee => ({
      x: Math.random() * W,
      y: 40 + Math.random() * Math.max(1, H - 80),
      a: Math.random() * Math.PI * 2,
      v: 34 + Math.random() * 28,
      turn: 0,
      seed: Math.random() * 1000,
      drop: Math.random() * 300,
    });

    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      const want = W < 640 ? 6 : 12;
      while (bees.length < want) bees.push(makeBee());
      bees = bees.slice(0, want);
    };

    const drawBee = (bee: Bee, t: number) => {
      g.save();
      g.translate(bee.x, bee.y);
      g.rotate(bee.a);
      g.scale(SIZE, SIZE);
      const flap = 0.55 + 0.45 * Math.abs(Math.sin(t / 45 + bee.seed));
      g.fillStyle = 'rgba(255, 255, 255, 0.9)';
      g.strokeStyle = 'rgba(28, 26, 23, 0.3)';
      g.lineWidth = 0.6;
      for (const side of [-1, 1]) {
        g.beginPath();
        g.ellipse(-1, side * 3.6 * flap, 3.4, 2.2 * flap, side * 0.5, 0, Math.PI * 2);
        g.fill();
        g.stroke();
      }
      g.fillStyle = '#ffc83d';
      g.strokeStyle = 'rgba(28, 26, 23, 0.75)';
      g.lineWidth = 0.9;
      g.beginPath();
      g.ellipse(0, 0, 5.2, 3.4, 0, 0, Math.PI * 2);
      g.fill();
      g.stroke();
      g.fillStyle = '#1c1a17';
      g.fillRect(-1.8, -3.2, 1.3, 6.4);
      g.fillRect(1, -3.2, 1.3, 6.4);
      g.beginPath();
      g.arc(5.6, 0, 2.1, 0, Math.PI * 2);
      g.fill();
      g.restore();
    };

    const drawCheck = (c: Check, age: number) => {
      const k = age / 1500;
      g.save();
      g.globalAlpha = Math.max(0, 0.7 * (1 - k));
      g.translate(c.x, c.y);
      g.scale(SIZE * (0.8 + k * 0.4), SIZE * (0.8 + k * 0.4));
      g.strokeStyle = '#1c1a17';
      g.lineWidth = 1.8;
      g.lineCap = 'round';
      g.lineJoin = 'round';
      g.beginPath();
      g.moveTo(-3, 0);
      g.lineTo(-1, 2.2);
      g.lineTo(3.2, -2.4);
      g.stroke();
      g.restore();
    };

    const frame = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (!document.hidden) {
        g.clearRect(0, 0, W, H);
        checks = checks.filter((c) => now - c.t < 1500);
        for (const c of checks) drawCheck(c, now - c.t);
        for (const bee of bees) {
          // voo de abelha: vira aos poucos, com um zigue-zague leve, e volta quando chega na borda
          bee.turn += (Math.random() - 0.5) * 3 * dt;
          bee.turn *= 0.96;
          bee.a += bee.turn * dt * 6 + Math.sin(now / 300 + bee.seed) * 0.6 * dt;
          const m = 40;
          if (bee.x < m || bee.x > W - m || bee.y < m || bee.y > H - m) {
            const back = Math.atan2(H / 2 - bee.y, W / 2 - bee.x);
            bee.a += Math.atan2(Math.sin(back - bee.a), Math.cos(back - bee.a)) * 1.8 * dt;
          }
          bee.x += Math.cos(bee.a) * bee.v * dt;
          bee.y += Math.sin(bee.a) * bee.v * dt + Math.sin(now / 160 + bee.seed) * 0.2;
          bee.drop -= dt * 1000;
          if (bee.drop <= 0) {
            bee.drop = 260;
            checks.push({ x: bee.x - Math.cos(bee.a) * 7, y: bee.y - Math.sin(bee.a) * 7, t: now });
          }
          drawBee(bee, now);
        }
      }
      raf = requestAnimationFrame(frame);
    };

    resize();
    window.addEventListener('resize', resize);
    if (reduce) {
      // sem movimento: as abelhas paradas, sem rastro
      for (const bee of bees) drawBee(bee, 0);
    } else {
      raf = requestAnimationFrame(frame);
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={ref} className="bee-field" aria-hidden="true" data-testid="bee-field" />;
}
